import { useTranslation } from 'next-i18next';
import { useBjModal } from '@hooks/common/useBjModal';
import { useJobBenefits } from '@components/templates/job-add/edit/hooks/useJobBenefits';
import { CategorisedStaticBenefits, SetMirrorData, MirrorData } from '@type/job-add';
import {
  Button, Label,
  RecordInterface, SetFormValues, GlobalValuesTypes,
} from '@components/common/Forms';
import { SimpleEmployerProfileList } from '@components/templates/job-add/edit/functions/jobCompanyNameFunctions';
import { EmployerBenefitsList } from '@components/templates/job-add/edit/form/partials/EmployerBenefitsList';
import { hideUnselectedBenefits } from '@components/templates/job-add/edit/functions/jobBenefitsFunctions';
import { EditBenefitsModal } from '@components/templates/job-add/edit/modals/EditBenefitsModal';
import { AdjustmentsVerticalIcon } from '@heroicons/react/24/outline';
import { dataTestId } from '@services/data-test-id';

/** Props */
interface JobBenefitsListProps {
  jobTemplate?: ApiSchema<'JobModel2'>,
  benefits?: CategorisedStaticBenefits[],
  apiEmployerProfileList?: SimpleEmployerProfileList[],
  record: RecordInterface,
  setFormValues: SetFormValues,
  getFieldValue: (name: string) => GlobalValuesTypes,
  reqMirrorData?: Pick<MirrorData, 'companyName'>,
  setMirrorData?: SetMirrorData,
}

/**
 * @description Job benefits list component.
 * - `jobTemplate` - The job template.
 * - `benefits` - The adjusted benefits list.
 * - `apiEmployerProfileList` - The employer profile list.
 * - `record` - record function from useBjForm
 * - `setFormValues` - set form values function from useBjForm
 * - `getFieldValue` - get field value function from useBjForm
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData` - The set mirror data function.
 */
export const JobBenefitsList = (props: JobBenefitsListProps) => {
  // Destructure props
  const {
    benefits,
    jobTemplate,
    apiEmployerProfileList,
    record,
    setFormValues,
    getFieldValue,
    reqMirrorData,
    setMirrorData,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Use modal hook
  const {
    isOpen,
    toggleModal,
  } = useBjModal();

  // Use job benefits hook
  const {
    selectedBenefitsIds,
    setSelectedBenefitsIds,
    updateCurrentProfileBenefits,
  } = useJobBenefits({
    jobTemplate,
    setFormValues,
    apiEmployerProfileList,
    reqMirrorData,
    setMirrorData,
  });

  // Render component
  // *************************
  return (
    <>
      {/* Benefits list */}
      <Label
        as="div"
        description={t('global.benefits')}
        descriptionClass="font-bold text-ink-medium px-3.5"
      >
        <div className="grid grid-cols-1 gap-3">
          {/* Description tip */}
          <span className="block px-3.5 text-sm leading-snug text-ink-light">
            {t('global.benefits.label')}
          </span>

          {/* Benefits list */}
          <div className="rounded border border-dashed px-3.5 py-4">
            <EmployerBenefitsList
              benefits={hideUnselectedBenefits(selectedBenefitsIds, benefits)}
              record={record}
              type="list"
            />
          </div>

          {/* Open modal */}
          <Button
            color="light"
            styling="soft"
            size="sm"
            rounding="full"
            className="mx-3.5 gap-2 justify-self-start !py-1.5 !pl-3.5 !pr-4 !font-normal leading-tight"
            onClick={toggleModal}
            {...dataTestId('jobAddPage.jobBenefits.editBenefitsButton')}
          >
            <AdjustmentsVerticalIcon className="size-4" />
            {t('job.benefits.edit.text')}
          </Button>
        </div>
      </Label>

      {/* Edit benefits modal */}
      <EditBenefitsModal
        record={record}
        setFormValues={setFormValues}
        getFieldValue={getFieldValue}
        benefits={benefits}
        selectedBenefits={selectedBenefitsIds}
        setSelectedBenefits={setSelectedBenefitsIds}
        updateCurrentProfileBenefits={updateCurrentProfileBenefits}
        isOpen={isOpen}
        toggleModal={toggleModal}
      />
    </>
  );
};
